import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { COUNTRY_FOR_LOCALE } from "../../../config";
import { CountryLocale, Locale } from "../Languages";
import { makeGraphqlRequest } from "./makeGraphqlRequest";
import { useClarity } from "./useClarity";
import { useGoogleTag } from "./useGoogleTag";
import { useIsSsr } from "./useIsSsr";
import { useLinkedIn } from "./useLinkedInTag";
import { useMetaPixel } from "./useMetaPixel";

const LS_TRACKING_CONSENT = "tracking_consent";
export const API_URL =
    process.env.NODE_ENV === "production" ? "https://d1az6j49u9mkuu.cloudfront.net/graphql/" : "http://localhost:3000/api";

const LS_VISITOR_ID = "visitor_id";
const getSetVisitorId = () => {
    let visitorId = localStorage.getItem(LS_VISITOR_ID);
    if (!visitorId) {
        visitorId = uuidv4();
        localStorage.setItem(LS_VISITOR_ID, visitorId);
    }
    return visitorId;
};

const getViewerIdForTracking = () => {
    const hasConsent = localStorage.getItem(LS_TRACKING_CONSENT) === "true";
    if (hasConsent) {
        return getSetVisitorId();
    } else {
        return undefined;
    }
};

type TrackingEvent = {
    type: "Page" | "Interaction" | "Conversion";
    page?: string;
    name: string;
    properties?: Record<string, any>;
};
export const useTracking = () => {
    let urlLocale =
        typeof window !== "undefined" ? (window.location.pathname.split("/")[1] as Lowercase<Locale>) : "de-de";

    const parsedlocale = [
        CountryLocale.AT,
        CountryLocale.CH,
        CountryLocale.DE,
        CountryLocale.GB,
        CountryLocale.PL,
        CountryLocale.ES,
        CountryLocale.FR,
        CountryLocale.CZ,
        CountryLocale.IT,
        CountryLocale.PT,
        CountryLocale.BR,
        CountryLocale.SE,
        CountryLocale.RO,
        CountryLocale.MX,
        CountryLocale.GR,
        CountryLocale.TR,
        CountryLocale.BE,
        CountryLocale.LT,
        CountryLocale.LV,
        CountryLocale.EE,
        CountryLocale.NL,
        "fr-be",
        CountryLocale.CA,
        "fr-ca",
        CountryLocale.TN,
        CountryLocale.MA,
    ].includes(urlLocale)
        ? urlLocale
        : undefined;

    const track = (event: TrackingEvent) => {
        const viewerId = getViewerIdForTracking();
        makeGraphqlRequest({
            operationName: "trackHomepageInteraction",
            query: `
                mutation trackHomepageInteraction($viewerId: ID, $events: [WebsiteTrackingEvent!]!) {
                    trackHomepageInteraction(viewerId: $viewerId, events: $events)
                }
            `,
            variables: {
                events: [
                    {
                        ...event,
                        properties: {
                            browserLocale: navigator.language,
                            referrer: document.referrer,
                            search: Object.fromEntries(new URLSearchParams(window.location.search)),
                            selectedLocale: parsedlocale,
                            userAgent: window.navigator.userAgent,
                            ...(event.properties ?? {}),
                        },
                    },
                ],
                viewerId: viewerId,
            },
        })
            .then((response) => response.json())
            .then((response) => {})
            .catch((err) => console.error(err));
    };

    const isSsr = useIsSsr();

    const [hasConsent, setHasConsent] = useState(false);
    const [showPopup, setShowPopup] = useState(
        !isSsr && (localStorage.getItem(LS_TRACKING_CONSENT) === null ? true : false),
    );
    const setConsent = (consent: boolean) => {
        localStorage.setItem(LS_TRACKING_CONSENT, consent.toString());
        setHasConsent(consent);
        setShowPopup(false);

        // consent is switched to yes
        if (consent) {
            getSetVisitorId();
            try {
                // set clarity cookies if user has given consent
                // @ts-ignore
                window.clarity && window.clarity("consent", true);
                // console.log("set clarity consent");
                // @ts-ignore
                window.dataLayer = window.dataLayer || [];
                function gtag() {
                    // @ts-ignore
                    dataLayer.push(arguments);
                }
                // @ts-ignore
                gtag("consent", "update", {
                    ad_personalization: "granted",
                    ad_storage: "granted",
                    ad_user_data: "granted",
                    analytics_storage: "granted",
                });

                // @ts-ignore
                window.fbq && window.fbq("consent", "grant");
            } catch (e) {
                console.log("could not set consents");
                console.log(e);
            }
        } else {
            // remove all cookies
            // remove clarity consent
            // @ts-ignore
            !!window.clarity && window.clarity("consent", false);

            // remove meta consent
            // @ts-ignore
            window.fbq && window.fbq("consent", "revoke");

            // @ts-ignore
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                // @ts-ignore
                dataLayer.push(arguments);
            }
            // remove google consent
            !!gtag &&
                // @ts-ignore
                gtag("consent", "update", {
                    ad_personalization: "denied",
                    ad_storage: "denied",
                    ad_user_data: "denied",
                    analytics_storage: "denied",
                });

            // remove reonic cookie
            localStorage.removeItem(LS_VISITOR_ID);
        }
    };

    useEffect(() => {
        setHasConsent(localStorage.getItem(LS_TRACKING_CONSENT) === "true");
    }, []);

    return { hasConsent, setConsent, showPopup, track };
};

export const TrackPageVisit = ({ extras, locale }: { extras?: Record<string, string>; locale: Locale }) => {
    const { track, hasConsent } = useTracking();

    React.useEffect(() => {
        track({
            name: window.location.pathname,
            page: window.location.pathname,
            type: "Page",
        });
        return;
    }, []);

    useClarity(hasConsent);
    useGoogleTag(hasConsent);
    useMetaPixel(hasConsent, COUNTRY_FOR_LOCALE[locale]);
    // useHubspot();
    useLinkedIn(hasConsent);

    return <></>;
};
